import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { CheckCircledIcon, CrossCircledIcon, InfoCircledIcon, Cross1Icon } from '@radix-ui/react-icons';
import { cn } from '../../utils/cn';

/**
 * App-wide toast notifications.
 *
 * Before this existed, feedback was improvised per feature: six `alert()`
 * calls, one bespoke toast in StaffAccessManagement, and many actions with no
 * feedback at all (retry, refresh, client-created all logged to console).
 * `alert()` blocks the whole tab and looks like a browser error - the opposite
 * of what a "your reputation is being handled" product should feel like.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success('Schedule paused');
 *   toast.error('Could not create the scan', { description: err.message });
 */

const ToastContext = createContext(null);

const VARIANTS = {
	success: { icon: CheckCircledIcon, iconClass: 'text-green-600' },
	error: { icon: CrossCircledIcon, iconClass: 'text-red-600' },
	info: { icon: InfoCircledIcon, iconClass: 'text-brand-600' },
};

const AUTO_DISMISS_MS = 5000;

export function ToastProvider({ children }) {
	const [toasts, setToasts] = useState([]);
	const idRef = useRef(0);

	const dismiss = useCallback((id) => {
		setToasts((t) => t.filter((x) => x.id !== id));
	}, []);

	const push = useCallback(
		(variant, title, opts = {}) => {
			const id = ++idRef.current;
			setToasts((t) => [...t.slice(-3), { id, variant, title, description: opts.description }]);
			// Errors persist until dismissed - a failure notice that removes
			// itself while the user is looking away was never seen at all.
			if (variant !== 'error') {
				setTimeout(() => dismiss(id), opts.duration || AUTO_DISMISS_MS);
			}
			return id;
		},
		[dismiss]
	);

	const api = useRef({
		success: (title, opts) => push('success', title, opts),
		error: (title, opts) => push('error', title, opts),
		info: (title, opts) => push('info', title, opts),
		dismiss,
	});
	// Keep the stable ref pointing at the latest push/dismiss.
	api.current.success = (title, opts) => push('success', title, opts);
	api.current.error = (title, opts) => push('error', title, opts);
	api.current.info = (title, opts) => push('info', title, opts);
	api.current.dismiss = dismiss;

	return (
		<ToastContext.Provider value={api.current}>
			{children}
			{/* aria-live so screen readers announce results of async actions */}
			<div
				aria-live="polite"
				className="pointer-events-none fixed bottom-4 right-4 z-[60] flex w-full max-w-sm flex-col gap-2"
			>
				{toasts.map((t) => {
					const v = VARIANTS[t.variant] || VARIANTS.info;
					const Icon = v.icon;
					return (
						<div
							key={t.id}
							className="pointer-events-auto flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card-hover animate-in slide-in-from-bottom-2 fade-in-0"
						>
							<Icon className={cn('mt-0.5 h-5 w-5 shrink-0', v.iconClass)} />
							<div className="min-w-0 flex-1">
								<p className="text-sm font-medium text-foreground">{t.title}</p>
								{t.description && (
									<p className="mt-0.5 text-sm text-muted-foreground break-words">{t.description}</p>
								)}
							</div>
							<button
								type="button"
								onClick={() => dismiss(t.id)}
								className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand-500"
							>
								<Cross1Icon className="h-3.5 w-3.5" />
								<span className="sr-only">Dismiss</span>
							</button>
						</div>
					);
				})}
			</div>
		</ToastContext.Provider>
	);
}

export function useToast() {
	const ctx = useContext(ToastContext);
	if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
	return ctx;
}
